import { useEffect, useState } from 'react'

import { Check, ChevronUp, LoaderCircle, X } from 'lucide-react'
import { ButtonIcon, SheetBody, SheetFooter, cn } from 'twyne-new.ui'

import BulkPublishDetails from './Details'
import { BulkPublishStateProvider, FINISHED, IDLE, RUNNING, useBulkPublish } from './context'

export default function BulkPublishProvider({ children }) {
  return <BulkPublishStateProvider>
    {children}
    <BulkPublishFooter />
  </BulkPublishStateProvider>
}

function BulkPublishFooter() {
  const { status, summary, close, iframeRef, iframeSrc } = useBulkPublish()
  const { total, published, failed, errors, current, position } = summary

  const [expanded, setExpanded] = useState(false)

  const handleToggle = () => setExpanded(expanded => !expanded)

  const handleClose = event => {
    event.stopPropagation()
    close()
  }

  const open = status !== IDLE
  const visible = useSlideIn(open)
  const running = status === RUNNING

  useEffect(() => {
    if (!open) {
      setExpanded(false)
    }
  }, [open])

  return <>
    {iframeSrc ? <iframe ref={iframeRef} title='bulk-publish-runner' className='hidden' src={iframeSrc} /> : null}
    {open || visible ? <div className={cn(
      'fixed inset-x-0 bottom-0 z-50 flex flex-col transition-all duration-300 ease-out',
      open && visible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0')
    }>
      {expanded ? <div className='w-[420px] max-w-full mr-auto rounded-t-md border-t border-r twy-border-base twy-bg-base-accent overflow-hidden animate-in fade-in-0 slide-in-from-bottom-2 duration-200'>
        <div className='p-4 twy-bg-base flex justify-between items-center twy-text-base-accent text-lg sticky top-0 z-10'>
          <span className='truncate max-w-[95%]'>
            {running ? `Publishing ${total} ${total === 1 ? 'page' : 'pages'}` : 'Publish finished'}
          </span>
          <ButtonIcon onClick={() => setExpanded(false)}>
            <X size={20} />
          </ButtonIcon>
        </div>
        <SheetBody className='p-0 max-h-[50vh] overflow-y-auto'>
          <BulkPublishDetails />
        </SheetBody>
        <SheetFooter className='justify-between'>
          <span className='text-sm twy-text-base-accent font-bold'>
            {running ? 'You can keep working while this runs.' : failed ? `${published} published, ${failed} failed.` : 'All pages published.'}
          </span>
        </SheetFooter>
      </div> : null}
      <div className='w-full h-6 flex items-center bg-gray-1600 text-sm font-semibold uppercase'>
        <div
          onClick={handleToggle}
          className='h-full flex items-center gap-2 px-3 cursor-pointer text-gray-600 transition-colors hover:bg-gray-1200 hover:text-white'
        >
          {running ? <LoaderCircle className='animate-spin' size={12} /> : <Check size={12} />}
          <span className='truncate' title={errors.map(({ title, error }) => `${title}: ${error}`).join('\n') || undefined}>
            {running ? `Publishing ${position}/${total}${current ? ` · [#${current.id}] ${current.title}` : ''}` : `${published} published${failed ? ` · ${failed} failed` : ''}`}
          </span>
          <ChevronUp size={14} className={cn('transition-transform', expanded && 'rotate-180')} />
          {status === FINISHED ? <span
            onClick={handleClose}
            className='cursor-pointer hover:text-white'
          >
            <X size={14} />
          </span> : null}
        </div>
      </div>
    </div> : null}
  </>
}

function useSlideIn(open) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }

    const timer = setTimeout(() => setVisible(false), 300)
    return () => clearTimeout(timer)
  }, [open])

  return visible
}
